// (2026-05-23) Vista HTML responsive de la Guia de Despacho publica
// (escaneo QR). Reemplaza el iframe-con-PDF que no rendia en moviles.
// Estructura coherente con PublicInvoiceView pero adaptada al shape de
// /api/documents/guia-despacho.
import React, { useState } from "react";
import { generateDispatchGuidePDF } from "../utils/pdf/DispacheGuidesPDF/generateDispacheGuidesPDF";
// (2026-08-14, defecto B) Fuente única de la comparación del estatus, compartida
// con el PDF de la guía. Ver `utils/guiaEstatus.js`.
import { esGuiaAnulada } from "../utils/guiaEstatus";

const fmt = (n, d = 2) => {
  if (n === null || n === undefined || n === "") return "—";
  const num = Number(n);
  if (Number.isNaN(num)) return String(n);
  return num.toLocaleString("es-VE", { minimumFractionDigits: d, maximumFractionDigits: d });
};

// (2026-08-13) ESTATUS ANULADA — comparación ROBUSTA, no sensible a mayúsculas
// ni a espacios.
//
// (2026-08-14, defecto B) La función VIVÍA AQUÍ y el PDF de la guía tenía su
// propia comparación `=== "anulada"`, sensible a mayúsculas: con el estatus
// 'ANULADA' que el alta llegó a persistir como texto libre, el PDF salía SIN
// sello mientras esta vista sí avisaba. Se movió a `utils/guiaEstatus.js` para
// que las dos capas compartan UNA sola verdad; allí está el porqué completo.
// Se re-exporta desde aquí para no romper la superficie pública del módulo.
export { ESTATUS_GUIA_ANULADA, esGuiaAnulada } from "../utils/guiaEstatus";

// (2026-08-13) `documento.fecha_anulacion` viaja como ISO-8601 YA convertido a
// America/Caracas por el backend (`_format_date` -> `a_hora_vzla(d).isoformat()`,
// documento_generation_routes.py:95-110, emitido en :1733; `null` si la guía no
// está anulada).
//
// Se formatea LEYENDO LOS COMPONENTES DEL TEXTO, sin pasar por `new Date()`:
// re-interpretar la cadena con Date la re-expresaría en la zona horaria del
// DISPOSITIVO que escanea el QR, y una anulación de las 21:00 de Caracas se
// mostraría con otra hora —y a veces con otro día— según dónde esté el fiscal.
// Es la misma trampa ya documentada en `PublicInvoiceView.fmtFecha`.
//
// Devuelve `null` cuando no hay dato: el aviso NO inventa fecha.
export const fmtFechaHoraVzla = (s) => {
  if (s === null || s === undefined || s === "") return null;
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/);
  if (!m) return String(s);
  const fecha = `${m[3]}/${m[2]}/${m[1]}`;
  return m[4] ? `${fecha} a las ${m[4]}:${m[5]}` : fecha;
};

const PublicDispatchGuideView = ({ data, realId }) => {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");
  if (!data) return null;

  const emisor       = data.emisor || {};
  const destinatario = data.destinatario || {};
  const documento    = data.documento || {};
  const transporte   = data.transporte || {};
  const items        = data.items || [];
  const totales      = data.totales || {};
  const refFactura   = data.referencia_factura || {};
  const legal        = data.legal || {};

  const anulada        = esGuiaAnulada(documento.estatus);
  const fechaAnulacion = fmtFechaHoraVzla(documento.fecha_anulacion);

  const handleDownload = async () => {
    try {
      setDownloading(true); setError("");
      await generateDispatchGuidePDF(realId || documento.qr_token, "download");
    } catch (e) {
      setError(e?.message || "No se pudo generar el PDF.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="pdv-root">
      <div className="pdv-topbar">
        {/* (2026-05-26 v17) URL relativa — mismo dominio que el frontend */}
        <img src="/quantus-logo.png" alt="Quantus Invoice" className="pdv-topbar-logo" />
        <span className="pdv-topbar-by">by Sirius Solutions</span>
      </div>

      <div className="pdv-page">
        {/* (2026-08-13) AVISO "GUÍA ANULADA" — PRIMER elemento de la página, sin
            necesidad de desplazar. Espeja el banner "DOCUMENTO ANULADO" de
            `PublicInvoiceView` (mismo rojo #dc2626, mismo peso y espaciado):
            esta vista es la que abre quien escanea el QR en la alcabala, y hasta
            hoy una guía anulada sólo se distinguía por el renglón "Estatus" del
            recuadro de datos — es decir, no se distinguía.

            LA ATRIBUCIÓN DE LA CAUSA NO ES UNA SUPOSICIÓN, Y ESTÁ ACOTADA A LAS
            GUÍAS: sobre `GuiaDespacho`, `fecha_anulacion` se escribe en un único
            punto del backend —`factura_routes._anular_guia_de`, la cascada de la
            anulación de la factura— y el alta de la guía ni siquiera acepta esa
            columna (`documentos_routes.crear_guia_despacho`, constructor de
            `GuiaDespacho`).

            CORRECCIÓN (2026-08-13): hasta hoy este comentario decía «un único
            punto de TODO el backend», y eso era falso. `fecha_anulacion` se
            escribe en TRES sitios; los otros dos son de `Factura`, no de
            `GuiaDespacho`, y viven en `factura_routes.anular_factura` (la
            factura que se anula y sus facturas de comisión vinculadas). La
            afirmación acotada a la guía —que es la que sostiene este aviso— sí
            se cumple. Se citan por FUNCIÓN y no por número de línea a
            propósito: el número que había aquí (factura_routes.py:5012) ya se
            había quedado obsoleto por desplazamiento del archivo.

            Por eso la frase causal se pinta SÓLO cuando viene la fecha: si la
            guía llegara a 'anulada' por el texto libre del alta, no habría
            fecha y el aviso se limita a lo que sí consta. */}
        {anulada && (
          <div className="pdv-anulada" role="alert">
            <div className="pdv-anulada-titulo">GUÍA ANULADA</div>
            {fechaAnulacion && (
              <div className="pdv-anulada-detalle">
                Anulada el {fechaAnulacion} por la anulación de su factura
                {refFactura.numero_documento ? ` Nro. ${refFactura.numero_documento}` : ""}.
              </div>
            )}
            <div className="pdv-anulada-detalle">
              Esta guía NO ampara el traslado de la mercancía.
            </div>
          </div>
        )}

        {/* Etiqueta principal */}
        <div className="pdv-banner pdv-banner-dg">
          <div className="pdv-banner-label">GUÍA DE DESPACHO</div>
          <div className="pdv-banner-numero">{documento.numero_guia || "—"}</div>
        </div>

        {legal.sin_credito_fiscal && legal.leyenda && (
          <div className="pdv-legal-strong">{legal.leyenda}</div>
        )}

        {/* Emisor + Documento */}
        <div className="pdv-grid-2">
          <div className="pdv-card">
            <div className="pdv-card-title">EMISOR</div>
            <div className="pdv-card-body">
              <div className="pdv-strong">{emisor.nombre_empresa || "—"}</div>
              <div className="pdv-muted">RIF: {emisor.rif || "—"}</div>
              {emisor.denominacion_comercial && <div className="pdv-small">{emisor.denominacion_comercial}</div>}
              {emisor.direccion_fiscal && <div className="pdv-small">{emisor.direccion_fiscal}</div>}
              {emisor.telefono && <div className="pdv-small">Tel: {emisor.telefono}</div>}
              {emisor.email && <div className="pdv-small">{emisor.email}</div>}
            </div>
          </div>
          <div className="pdv-card pdv-card-doc">
            <div className="pdv-card-title">DATOS DE LA GUÍA</div>
            <div className="pdv-card-body">
              <div className="pdv-kv"><span>Nº Guía</span><strong>{documento.numero_guia || "—"}</strong></div>
              <div className="pdv-kv"><span>Nº Control</span><strong>{documento.numero_control_guia || "—"}</strong></div>
              <div className="pdv-kv"><span>Fecha salida</span><strong>{documento.fecha_salida || "—"}</strong></div>
              <div className="pdv-kv"><span>Hora salida</span><strong>{documento.hora_salida || "—"}</strong></div>
              {/* (2026-08-13) El renglón sigue mostrando el valor REAL de la
                  columna (sin normalizar, sin inventar): sólo se tiñe de rojo
                  cuando la comparación robusta dice que está anulada. */}
              <div className="pdv-kv">
                <span>Estatus</span>
                <strong className={anulada ? "pdv-estatus-anulada" : undefined}>
                  {documento.estatus || "—"}
                </strong>
              </div>
            </div>
          </div>
        </div>

        {/* Destinatario */}
        <div className="pdv-card">
          <div className="pdv-card-title">DESTINATARIO</div>
          <div className="pdv-card-body pdv-grid-3">
            <div><div className="pdv-muted-label">Razón social</div><div className="pdv-strong">{destinatario.nombre || "—"}</div></div>
            <div><div className="pdv-muted-label">RIF</div><div>{destinatario.rif || "—"}</div></div>
            <div><div className="pdv-muted-label">Teléfono</div><div>{destinatario.telefono || "—"}</div></div>
            <div className="pdv-col-span-3"><div className="pdv-muted-label">Dirección</div><div>{destinatario.direccion || "—"}</div></div>
          </div>
        </div>

        {/* Transporte */}
        {(transporte.origen || transporte.destino || transporte.transportista) && (
          <div className="pdv-card">
            <div className="pdv-card-title">TRANSPORTE</div>
            <div className="pdv-card-body pdv-grid-3">
              {transporte.origen && <div><div className="pdv-muted-label">Origen</div><div>{transporte.origen}</div></div>}
              {transporte.destino && <div><div className="pdv-muted-label">Destino</div><div>{transporte.destino}</div></div>}
              {transporte.motivo_traslado && <div><div className="pdv-muted-label">Motivo</div><div>{transporte.motivo_traslado}</div></div>}
              {transporte.transportista && <div><div className="pdv-muted-label">Transportista</div><div>{transporte.transportista}</div></div>}
              {transporte.chofer && <div><div className="pdv-muted-label">Chofer</div><div>{transporte.chofer}</div></div>}
              {transporte.placa && <div><div className="pdv-muted-label">Placa</div><div>{transporte.placa}</div></div>}
            </div>
          </div>
        )}

        {/* Items */}
        <div className="pdv-card">
          <div className="pdv-card-title">MERCANCÍA</div>
          <div className="pdv-items-desktop">
            <table className="pdv-items-table">
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>Descripción</th>
                  <th className="pdv-right">Cantidad</th>
                  <th>U/M</th>
                  <th className="pdv-right">Masa (kg)</th>
                  <th className="pdv-right">Volumen (m³)</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && <tr><td colSpan={6} className="pdv-empty">Sin mercancía registrada.</td></tr>}
                {items.map((it, i) => (
                  <tr key={i}>
                    <td>{it.sku || ""}</td>
                    <td>{it.descripcion || ""}</td>
                    <td className="pdv-right">{fmt(it.cantidad)}</td>
                    <td>{it.unidad_medida || ""}</td>
                    <td className="pdv-right">{fmt(it.masa_kg)}</td>
                    <td className="pdv-right">{fmt(it.volumen, 4)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="pdv-items-mobile">
            {items.map((it, i) => (
              <div key={i} className="pdv-item-card">
                <div className="pdv-item-head">
                  <span className="pdv-muted-label">{it.sku || ""}</span>
                  <strong>{fmt(it.cantidad)} {it.unidad_medida || ""}</strong>
                </div>
                {it.descripcion && <div className="pdv-item-desc">{it.descripcion}</div>}
                <div className="pdv-item-meta">
                  <span>Masa <strong>{fmt(it.masa_kg)} kg</strong></span>
                  {Number(it.volumen) > 0 && <span>Vol <strong>{fmt(it.volumen, 4)} m³</strong></span>}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Totales */}
        <div className="pdv-totales">
          <div className="pdv-total-row"><span>Total bultos</span><strong>{fmt(totales.total_bultos)}</strong></div>
          <div className="pdv-total-row"><span>Total masa</span><strong>{fmt(totales.total_masa_kg)} kg</strong></div>
        </div>

        {/* Referencia a factura */}
        {(refFactura.numero_documento || refFactura.numero_factura) && (
          <div className="pdv-card">
            <div className="pdv-card-title">FACTURA VINCULADA</div>
            <div className="pdv-card-body pdv-grid-3">
              <div><div className="pdv-muted-label">Nº Factura</div><div>{refFactura.numero_documento || refFactura.numero_factura}</div></div>
              <div><div className="pdv-muted-label">Nº Control</div><div>{refFactura.numero_control}</div></div>
              <div><div className="pdv-muted-label">Fecha</div><div>{refFactura.fecha_emision || "—"}</div></div>
            </div>
          </div>
        )}

        {/* Acciones */}
        <div className="pdv-actions">
          <button onClick={handleDownload} disabled={downloading} className="pdv-btn-pdf">
            {downloading ? "Generando PDF…" : "Descargar PDF"}
          </button>
          {error && <div className="pdv-error">{error}</div>}
        </div>

        <div className="pdv-footer">
          Documento emitido a través de <strong>Quantus Invoice</strong>.
          <br /><span className="pdv-muted-label">Powered by Sirius Solutions</span>
        </div>
      </div>

      <style>{PDV_STYLES}</style>
    </div>
  );
};

const PDV_STYLES = `
/* (2026-08-13) Tokens del aviso de anulación. Los tamaños viven aquí como
   variables —no repartidos en estilos inline— para que el ajuste a móvil sea
   UNA redefinición y no una segunda copia de las reglas. */
.pdv-root {
  --pdv-anulada-bg: #dc2626;
  --pdv-anulada-fg: #ffffff;
  --pdv-fs-anulada-titulo: 20px;
  --pdv-fs-anulada-detalle: 15px;
  --pdv-lh-anulada: 1.5;
}
@media (max-width:600px) {
  .pdv-root {
    --pdv-fs-anulada-titulo: 17px;
    --pdv-fs-anulada-detalle: 14px;
  }
}
.pdv-anulada {
  background: var(--pdv-anulada-bg); color: var(--pdv-anulada-fg);
  text-align: center; padding: 12px 16px; border-radius: 10px;
  margin: 0 0 16px; line-height: var(--pdv-lh-anulada);
}
.pdv-anulada-titulo {
  font-size: var(--pdv-fs-anulada-titulo); font-weight: 800; letter-spacing: 0.15em;
}
.pdv-anulada-detalle {
  font-size: var(--pdv-fs-anulada-detalle); font-weight: 600;
  letter-spacing: 0.02em; margin-top: 4px;
}
.pdv-estatus-anulada { color: var(--pdv-anulada-bg); font-weight: 700; }
.pdv-root { background:#f8fafc; min-height:100vh; color:#0f172a;
  font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif; }
.pdv-topbar { background:#0f172a; padding:12px 16px; display:flex; align-items:center; justify-content:center; gap:8px; }
.pdv-topbar-logo { height:28px; width:auto; }
.pdv-topbar-by { color:#cbd5e1; font-size:11px; letter-spacing:0.04em; }
.pdv-page { max-width:760px; margin:0 auto; padding:16px; }
.pdv-banner { padding:14px 18px; border-radius:8px; margin-bottom:14px;
  display:flex; flex-wrap:wrap; justify-content:space-between; align-items:center; gap:8px; }
.pdv-banner-dg { background:#0e7490; color:#fff; }
.pdv-banner-label { font-weight:700; letter-spacing:0.04em; }
.pdv-banner-numero { font-size:20px; font-weight:800; letter-spacing:0.04em; }
.pdv-legal-strong {
  background:#fef9c3; border:1px solid #fde047; color:#713f12;
  padding:10px 14px; border-radius:6px; font-weight:700; font-size:12px;
  text-align:center; margin-bottom:14px;
}
.pdv-grid-2 { display:grid; grid-template-columns:1fr 1fr; gap:12px; margin-bottom:14px; }
@media (max-width:600px) { .pdv-grid-2 { grid-template-columns:1fr; } }
.pdv-card { background:#fff; border:1px solid #e2e8f0; border-radius:8px; margin-bottom:14px; overflow:hidden; }
.pdv-card-title { background:#1e293b; color:#cbd5e1; padding:8px 14px;
  font-size:11px; font-weight:700; letter-spacing:0.08em; }
.pdv-card-doc .pdv-card-title { background:#0e7490; color:#fff; }
.pdv-card-body { padding:14px; font-size:13px; line-height:1.6; }
.pdv-strong { font-weight:700; font-size:14px; }
.pdv-muted { color:#64748b; }
.pdv-small { color:#475569; font-size:12px; }
.pdv-muted-label { color:#64748b; font-size:11px; letter-spacing:0.04em; text-transform:uppercase; }
.pdv-grid-3 { display:grid; grid-template-columns:repeat(3,1fr); gap:10px; }
.pdv-col-span-3 { grid-column:span 3; }
@media (max-width:600px) { .pdv-grid-3 { grid-template-columns:1fr 1fr; } .pdv-col-span-3 { grid-column:span 2; } }
.pdv-kv { display:flex; justify-content:space-between; gap:8px; padding:2px 0; }
.pdv-kv span { color:#64748b; font-size:12px; }
.pdv-kv strong { font-weight:600; }
.pdv-items-table { width:100%; border-collapse:collapse; font-size:12px; }
.pdv-items-table th { background:#f1f5f9; padding:8px 10px; text-align:left; font-weight:600; border-bottom:1px solid #e2e8f0; }
.pdv-items-table td { padding:8px 10px; border-bottom:1px solid #f1f5f9; vertical-align:top; }
.pdv-right { text-align:right; }
.pdv-empty { padding:16px; text-align:center; color:#94a3b8; font-style:italic; }
.pdv-items-mobile { display:none; padding:6px 10px 10px; }
@media (max-width:600px) { .pdv-items-desktop { display:none; } .pdv-items-mobile { display:block; } }
.pdv-item-card { background:#f8fafc; border:1px solid #e2e8f0; border-radius:6px; padding:10px 12px; margin-bottom:8px; }
.pdv-item-head { display:flex; justify-content:space-between; align-items:baseline; }
.pdv-item-desc { font-size:13px; margin:6px 0; }
.pdv-item-meta { display:flex; gap:12px; flex-wrap:wrap; font-size:12px; color:#475569; }
.pdv-totales { background:#fff; border:1px solid #e2e8f0; border-radius:8px; padding:14px 16px; margin-bottom:14px; }
.pdv-total-row { display:flex; justify-content:space-between; padding:6px 0; font-size:14px; }
.pdv-total-row span { color:#475569; }
.pdv-actions { display:flex; flex-direction:column; align-items:center; gap:8px; margin:20px 0; }
.pdv-btn-pdf { background:#1e293b; color:#fff; border:none; cursor:pointer;
  padding:12px 28px; border-radius:8px; font-size:14px; font-weight:600;
  box-shadow:0 2px 6px rgba(0,0,0,0.12); transition:transform 120ms, background 120ms; }
.pdv-btn-pdf:hover:not(:disabled) { background:#0f172a; transform:translateY(-1px); }
.pdv-btn-pdf:disabled { opacity:0.6; cursor:wait; }
.pdv-error { color:#dc2626; font-size:12px; }
.pdv-footer { text-align:center; font-size:11px; color:#64748b; padding:16px;
  margin-top:16px; border-top:1px solid #e2e8f0; background:#fff; }
`;

export default PublicDispatchGuideView;
